<template>
  <div class="card">
    <div class="card-header">
      <h5>{{ profile.displayName || t('profile.company.title') }}</h5>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="text-center py-4">
        <span class="spinner-border" role="status"></span>
        <p class="mt-2 mb-0">Loading company information...</p>
      </div>
      <div v-else>
        <div>
          <p>Email: {{ profile.email || 'Not specified' }}</p>
          <p>Phone: {{ profile.phone || 'Not specified' }}</p>
        </div>
        
        <!-- Manual Address Input with Autocomplete -->
        <div class="mb-3 position-relative">
          <label class="form-label">{{ t('profile.company.address') }}</label>
          <div class="input-group">
            <span class="input-group-text"><i class="ti ti-search"></i></span>
            <input 
              v-model="address" 
              type="text"
              class="form-control" 
              :placeholder="t('profile.map.addressPlaceholder')"
              @input="onAddressInput"
              @blur="onAddressBlur"
              @keyup.enter.exact.prevent="searchAddressOnMap"
              @keydown.down.prevent="selectNextSuggestion"
              @keydown.up.prevent="selectPreviousSuggestion"
              @keydown.enter.prevent="selectCurrentSuggestion"
              :disabled="!canEditTenantInfo"
            />
          </div>
          <!-- Address Suggestions Dropdown -->
          <div v-if="addressSuggestions.length > 0 && showSuggestions" class="address-suggestions">
            <div 
              v-for="(suggestion, index) in addressSuggestions" 
              :key="index"
              class="address-suggestion-item"
              :class="{ 'active': selectedSuggestionIndex === index }"
              @click="selectSuggestion(suggestion)"
              @mouseenter="selectedSuggestionIndex = index"
            >
              <i class="ti ti-map-pin me-2"></i>
              <span>{{ suggestion.place_name }}</span>
            </div>
          </div>
          <small class="form-text text-muted">{{ t('profile.map.addressHint') }}</small>
        </div>
        
        <!-- Map container -->
        <div class="row align-items-center">
          <!-- Map instruction text -->
          <div class="map-instructions mb-2">
            <i class="ti ti-hand-finger me-1"></i>
            <span>{{ t('profile.map.instructions') }}</span>
          </div>
          <div id="map-container" style="width: 100%; height: 250px; border-radius: 4px;"></div>
          
          <!-- Pin dropped confirmation -->
          <div v-if="isPinDragged" class="text-center mt-2 text-success pin-dropped-message">
            <i class="ti ti-check-circle me-1"></i>
            <span>{{ t('profile.map.pinUpdated') }}</span>
          </div>
        </div>
        
        <!-- Save button - only show if address changed -->
        <div v-if="addressChanged && canEditTenantInfo" class="mt-3 d-flex justify-content-end">
          <button 
            class="btn btn-primary" 
            @click="saveAddress" 
            :disabled="isSaving">
            <i v-if="isSaving" class="ti ti-loader ti-spin me-1"></i>
            {{ isSaving ? t('profile.map.saving') : t('profile.map.saveAddress') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, onUnmounted, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { useMapboxMap } from '@/composables/useMapboxMap';
import { useAddressAutocomplete } from '@/composables/useAddressAutocomplete';
import { useGeocoding } from '@/composables/useGeocoding';
import { useAddressManagement } from '@/composables/useAddressManagement';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export default {
  name: 'ProfileCompanyCard',
  props: {
    profile: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:address'],
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    
    // Initialize Mapbox token
    const mapboxToken = (() => {
      const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      if (!token) {
        console.warn('Mapbox access token not found. Please set VITE_MAPBOX_ACCESS_TOKEN in your .env file');
      } else {
        mapboxgl.accessToken = token;
      }
      return token;
    })();
    
    // Permission check
    const canEditTenantInfo = computed(() => hasPermission('Supplier.TenantInfo.Set'));
    const currentUser = computed(() => store.getters['auth/currentUser']);
    
    // Use composables
    const profileRef = computed(() => props.profile);
    const addressManagement = useAddressManagement(profileRef, emit);
    const geocoding = useGeocoding(mapboxToken);
    const autocomplete = useAddressAutocomplete(mapboxToken);
    const mapManagement = useMapboxMap(
      mapboxToken,
      canEditTenantInfo,
      {
        lat: props.profile.latitude || 41.0082,
        lng: props.profile.longitude || 28.9784
      }
    );
    
    // Helper: Create marker drag end handler
    const createMarkerDragHandler = () => {
      return async (lat, lng) => {
        addressManagement.updateLocation(lat, lng);
        const addressFromCoords = await geocoding.reverseGeocode(lat, lng);
        if (addressFromCoords) {
          addressManagement.updateAddress(addressFromCoords);
        }
      };
    };
    
    // Helper: Initialize map
    const initMap = () => {
      mapManagement.initMap('map-container', createMarkerDragHandler());
    };
    
    // Helper: Update map location from address management
    const syncMapLocation = () => {
      const { lat, lng } = addressManagement.currentLocation.value;
      if (lat && lng) {
        mapManagement.currentLocation.value = { lat, lng };
        mapManagement.updateMapLocation(lat, lng, 13);
      }
    };
    
    // Helper: Reverse geocode if needed
    const reverseGeocodeIfNeeded = () => {
      const { lat, lng } = addressManagement.currentLocation.value;
      if (lat && lng && !addressManagement.address.value) {
        geocoding.reverseGeocode(lat, lng).then(address => {
          if (address) {
            addressManagement.updateAddress(address);
          }
        });
      }
    };
    
    // Helper: Handle successful geocode result
    const handleGeocodeResult = (result) => {
      if (!result) {
        store.dispatch('notify', {
          message: t('profile.map.addressNotFound'),
          type: 'warning'
        });
        return;
      }
      
      addressManagement.updateLocation(result.lat, result.lng);
      addressManagement.updateAddress(result.address);
      mapManagement.updateMapLocation(result.lat, result.lng);
    };
    
    // Search address on map
    const searchAddressOnMap = async () => {
      if (!addressManagement.address.value.trim() || !mapboxToken) {
        return;
      }
      
      const result = await geocoding.forwardGeocode(addressManagement.address.value);
      handleGeocodeResult(result);
    };
    
    // Handle address input
    const handleAddressInput = () => {
      autocomplete.onAddressInput(addressManagement.address.value);
    };
    
    // Select suggestion
    const selectSuggestion = (suggestion) => {
      addressManagement.updateAddress(suggestion.place_name);
      autocomplete.clearSuggestions();
      
      const [lng, lat] = suggestion.center;
      addressManagement.updateLocation(lat, lng);
      mapManagement.updateMapLocation(lat, lng);
    };
    
    // Select current suggestion (keyboard)
    const selectCurrentSuggestion = () => {
      const index = autocomplete.selectedSuggestionIndex.value;
      const suggestions = autocomplete.addressSuggestions.value;
      
      if (index >= 0 && suggestions[index]) {
        selectSuggestion(suggestions[index]);
      } else {
        searchAddressOnMap();
      }
    };
    
    // Save address
    const saveAddress = async () => {
      await addressManagement.saveAddress(() => {
        const { lat, lng } = addressManagement.currentLocation.value;
        mapManagement.updateMapLocation(lat, lng);
      });
    };
    
    // Helper: Validate mapbox token
    const validateMapboxToken = () => {
      if (!mapboxToken) {
        store.dispatch('notify', {
          message: 'Mapbox access token is not configured. Please set VITE_MAPBOX_ACCESS_TOKEN in your .env file',
          type: 'error'
        });
        return false;
      }
      return true;
    };
    
    // Helper: Initialize map after address load
    const initializeMapAfterLoad = () => {
      syncMapLocation();
      initMap();
      syncMapLocation();
      reverseGeocodeIfNeeded();
    };
    
    // Load address and initialize map
    const initializeComponent = async () => {
      if (!validateMapboxToken()) {
        return;
      }
      
      await addressManagement.loadAddressFromApi();
      setTimeout(initializeMapAfterLoad, 100);
    };
    
    // Helper: Handle profile address change
    const handleProfileAddressChange = (newProfile) => {
      addressManagement.updateAddress(newProfile.address || '');
      addressManagement.resetOriginalAddress();
    };
    
    // Helper: Handle profile location change
    const handleProfileLocationChange = (newProfile) => {
      const lat = newProfile.latitude || 41.0082;
      const lng = newProfile.longitude || 28.9784;
      addressManagement.updateLocation(lat, lng);
      mapManagement.updateMapLocation(lat, lng, 13);
    };
    
    // Watch profile changes
    watch(() => props.profile, (newProfile, oldProfile) => {
      if (newProfile.address !== oldProfile?.address) {
        handleProfileAddressChange(newProfile);
      }
      
      if (newProfile.latitude !== oldProfile?.latitude || 
          newProfile.longitude !== oldProfile?.longitude) {
        handleProfileLocationChange(newProfile);
      }
    }, { deep: true });
    
    // Lifecycle hooks
    onMounted(() => {
      initializeComponent();
    });
    
    onUnmounted(() => {
      mapManagement.cleanup();
      autocomplete.cleanup();
    });
    
    return {
      address: addressManagement.address,
      isSaving: addressManagement.isSaving,
      isPinDragged: mapManagement.isPinDragged,
      addressChanged: addressManagement.addressChanged,
      searchAddressOnMap,
      onAddressChange: () => {},
      onAddressInput: handleAddressInput,
      onAddressBlur: autocomplete.onAddressBlur,
      saveAddress,
      currentUser,
      t,
      canEditTenantInfo,
      addressSuggestions: autocomplete.addressSuggestions,
      showSuggestions: autocomplete.showSuggestions,
      selectedSuggestionIndex: autocomplete.selectedSuggestionIndex,
      selectSuggestion,
      selectNextSuggestion: autocomplete.selectNextSuggestion,
      selectPreviousSuggestion: autocomplete.selectPreviousSuggestion,
      selectCurrentSuggestion
    };
  }
};
</script>

<style scoped>
.address-results {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.address-result-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.address-result-item:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

.address-result-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.map-instructions {
  font-size: 0.9rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  padding: 6px 12px;
  border-radius: 4px;
}

.pin-dropped-message {
  font-size: 0.9rem;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Mapbox map container styling */
#map-container {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 0;
}

/* Custom marker styling */
.custom-marker {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  transform: translate(-50%, -100%);
}

.custom-marker i {
  text-shadow: 0 0 2px white;
}

/* Address Suggestions Dropdown */
.address-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 2px;
}

.address-suggestion-item {
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
}

.address-suggestion-item:last-child {
  border-bottom: none;
}

.address-suggestion-item:hover,
.address-suggestion-item.active {
  background-color: rgba(0, 160, 223, 0.1);
  color: #00A0DF;
}

.address-suggestion-item i {
  color: #6c757d;
  font-size: 1rem;
}

.address-suggestion-item:hover i,
.address-suggestion-item.active i {
  color: #00A0DF;
}

/* Mapbox controls styling */
:deep(.mapboxgl-ctrl-group) {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.mapboxgl-ctrl-zoom-in),
:deep(.mapboxgl-ctrl-zoom-out) {
  width: 30px;
  height: 30px;
  border: none;
  background: white;
  color: #333;
}

:deep(.mapboxgl-ctrl-zoom-in):hover,
:deep(.mapboxgl-ctrl-zoom-out):hover {
  background: #f8f9fa;
}
</style>
